import { Reveal } from "./Reveal";

const MIC_URL =
  "https://images.pexels.com/photos/26280295/pexels-photo-26280295.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const About = () => (
  <section id="about" data-testid="about-section" className="border-t border-border">
    <div className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
      <div className="grid gap-16 md:grid-cols-2 md:items-center">
        <div>
          <Reveal>
            <p className="text-xs font-medium tracking-[0.35em] uppercase text-foreground/60">
              About Us
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-normal tracking-tight sm:text-4xl">
              Experience that connects music, business, and culture.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-lg text-base leading-relaxed text-foreground/80">
              Lakeview Music is a music and entertainment company founded by
              Greg Hammer, a veteran music executive with more than three
              decades of experience across record labels, artist development,
              A&amp;R, marketing, publishing, brand partnerships, management and
              entertainment strategy.
            </p>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-foreground/80">
              Greg has spent his career bringing music and business together —
              building companies, developing artists, creating marketing
              strategies, and helping brands use music to connect with
              audiences in meaningful ways.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.2} className="relative">
          <div className="absolute -left-4 -top-4 h-full w-full border border-border" />
          <img
            src={MIC_URL}
            alt="A vintage condenser microphone in a recording studio"
            data-testid="about-image"
            className="relative w-full border border-foreground/20 object-cover"
          />
        </Reveal>
      </div>

      <div className="mt-20 max-w-3xl">
        <Reveal>
          <p className="text-base leading-relaxed text-foreground/80">
            Most recently, Greg spent 18+ years as Managing Director of
            Red Bull Records, where he built the label from the ground up.
            During his tenure, the label developed a global roster that included
            AWOLNATION, Beartooth, Blxst, Albert Hammond Jr., The Aces, and
            others, generating Gold, Platinum and Diamond-certified records and
            billions of streams.
          </p>
          <p className="mt-6 font-serif text-xl italic text-foreground sm:text-2xl">
            But the experience goes well beyond running a record label.
          </p>
          <p className="mt-6 text-base leading-relaxed text-foreground/80">
            Before leading Red Bull Records, Greg held senior A&amp;R and
            marketing positions at Universal Records, Underdog Records and
            Abrupt Records, founded and operated his own music company, and
            worked directly with artists on everything from creative development
            and recording to marketing, touring, business management, and
            major-label negotiations. His career has included working with
            artists such as Sister Hazel, Tonic, Grinspoon, Shades Apart, and
            others, as well as projects including the American Pie soundtrack.
          </p>
        </Reveal>
      </div>

      <div className="mt-20 grid gap-16 md:grid-cols-2">
        <Reveal>
          <h3
            data-testid="about-brands-heading"
            className="font-serif text-2xl font-medium sm:text-3xl"
          >
            Where music meets brands
          </h3>
          <p className="mt-6 text-base leading-relaxed text-foreground/80">
            A defining part of Greg's career has been understanding how music
            can function as more than entertainment — it can be a powerful
            brand-building and cultural marketing platform.
          </p>
          <p className="mt-6 text-base leading-relaxed text-foreground/80">
            Through his work with Red Bull, Greg advised brands on music
            strategy, artist relationships, entertainment partnerships, live
            activations, content, and cultural initiatives. He helped incubate
            Red Bull Records itself, developed the strategy for Red Bull Music
            Studios, and worked across music festivals, events, artist
            partnerships, and brand activations.
          </p>
          <p className="mt-6 text-base leading-relaxed text-foreground/80">
            That perspective has produced campaigns recognized by some of the
            industry's most respected creative organizations, including a Clio
            Award for Music Marketing and a Cannes Lion for excellence in
            marketing. Greg also nurtured the development of innovative
            partnerships such as Blxst's Guide to LA with Apple Maps, creating
            a model that other artists subsequently followed.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <h3
            data-testid="about-operator-heading"
            className="font-serif text-2xl font-medium sm:text-3xl"
          >
            An operator's perspective
          </h3>
          <p className="mt-6 text-base leading-relaxed text-foreground/80">
            Greg brings something different to consulting: he has actually built
            and run the businesses he advises.
          </p>
          <p className="mt-6 text-base leading-relaxed text-foreground/80">
            He understands the creative side of the music business, but also the
            realities behind it — budgets, teams, deal structures, revenue,
            marketing, legal, operations, partnerships, and long-term strategy.
          </p>
          <p className="mt-6 text-base leading-relaxed text-foreground/80">
            At Red Bull Records, he integrated A&amp;R, marketing, finance,
            legal, brand integration, and operations into a single leadership
            structure designed to make better decisions and execute more
            effectively. That experience drives Lakeview Music's approach.
          </p>
          <p className="mt-6 text-base leading-relaxed text-foreground/80">
            We work with artists, labels, management companies, brands,
            investors, and entertainment businesses that want an experienced
            perspective on how to build, position, and grow in a rapidly
            changing music landscape.
          </p>
          <p className="mt-6 text-base leading-relaxed text-foreground/80">
            Whether the challenge is developing an artist strategy, evaluating a
            label or catalog opportunity, building a music business, creating a
            brand partnership, or navigating the increasingly complex
            relationship between music, culture, and commerce, Lakeview Music
            brings decades of real-world experience to the table.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <p
          data-testid="about-closing-line"
          className="mx-auto mt-24 max-w-2xl text-center font-serif text-2xl font-light leading-snug text-foreground sm:text-3xl"
        >
          Deep music expertise. Brand fluency. An operator's perspective.
          <span className="mt-3 block italic text-foreground/60">
            That's Lakeview Music.
          </span>
        </p>
      </Reveal>
    </div>
  </section>
);
